import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import { ErrorLabel } from '../../../components';
import { Button, PasswordInput } from '../../../compositions';
import { NewPasswordFormBase } from '../../../entities/@forms/ChangePasswordForm/ChangePasswordForm';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { passwordMinimalLength } from '../../../entities/ResetPassword/ResetPassword';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { ResetPasswordFormErrors, validateResetPasswordFormData } from './validations';

import './ResetPasswordForm.scss';

interface ResetPasswordFormProps extends FormProps<NewPasswordFormBase> {
    className?: string;
}

export const ResetPasswordForm: FC<ResetPasswordFormProps> = ({
    isLoading,
    isSuccessful,
    error,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const formRef = useRef<HTMLFormElement>(null);
    const [formErrors, setFormErrors] = useState<ResetPasswordFormErrors>({});

    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');

    useEffect((): void => {
        if (isSuccessful) {
            setNewPassword('');
            setConfirmNewPassword('');
        }
    }, [isSuccessful]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        const formData: NewPasswordFormBase = {
            newPassword,
            confirmNewPassword,
        };

        const [errors, hasErrors] = validateResetPasswordFormData(formData);

        setFormErrors(errors);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`reset-password-form ${className}`}
        >
            <h1 className="reset-password-form__heading">
                {trans('containers.resetPasswordForm.heading')}
            </h1>

            <p className="reset-password-form__instructions">
                {trans('containers.resetPasswordForm.instructions')}
            </p>

            <PasswordInput
                autoComplete="new-password"
                label={trans('containers.resetPasswordForm.newPasswordLabel')}
                placeholder={trans('containers.resetPasswordForm.newPasswordPlaceholder')}
                value={newPassword}
                minLength={passwordMinimalLength}
                error={formErrors.newPassword}
                disabled={isLoading}
                onChange={setNewPassword}
                className="reset-password-form__password-input"
                fieldClassName="reset-password-form__password-input-field"
            />

            <PasswordInput
                autoComplete="new-password"
                label={trans('containers.resetPasswordForm.confirmNewPasswordLabel')}
                placeholder={trans('containers.resetPasswordForm.confirmNewPasswordPlaceholder')}
                value={confirmNewPassword}
                minLength={passwordMinimalLength}
                error={formErrors.confirmNewPassword}
                disabled={isLoading}
                onChange={setConfirmNewPassword}
                className="reset-password-form__password-input"
                fieldClassName="reset-password-form__password-input-field"
            />

            <Button
                isSmall
                hasAnimation
                type="submit"
                text={trans('containers.resetPasswordForm.submitButton')}
                className="reset-password-form__submit-button"
            />

            {error && (
                <ErrorLabel
                    text={error}
                    className="reset-password-form__error-label"
                />
            )}
        </form>
    );
};
